import React, {useState, useEffect, useContext} from 'react';
import { Send, User, MessageSquare, Mail, ChevronDown } from 'lucide-react';
import { doApiGet, doApiMethod, API_URL } from '../services/apiService';
import {convertToHebrewDate} from "../utils/HebrewDateConverter";
import {FamilyContext} from "../Context/FamilyHeadContext";
import {useTranslation} from "react-i18next";

const Forum = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [comment, setComment] = useState('');
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const { isMainSite } = useContext(FamilyContext);
    const { t } = useTranslation()

    useEffect(() => {
        const fetchApprovedComments = async () => {
            try {
                const data = await doApiGet(`${API_URL}/messagesBazach/approved/${isMainSite}`);
                const sortedMessages = data.messages.sort((a, b) =>
                    new Date(b.createdAt) - new Date(a.createdAt)
                );
                setComments(sortedMessages);
            } catch (err) {
                console.error('Error fetching comments:', err);
                setError('שגיאה בטעינת התגובות');
            } finally {
                setIsLoading(false);
            }
        };

        fetchApprovedComments();
    }, []);

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (name && email && comment) {
            try {
                const formData = new FormData();
                formData.append('name', name);
                formData.append('email', email);
                formData.append('content', comment);

                await doApiMethod(`${API_URL}/messagesBazach`, "POST", formData);

                setName('');
                setEmail('');
                setComment('');
                alert(t('familyForum.success'));
            } catch (err) {
                alert(t('familyForum.error') + err.message);
            }
        }
    };

    return (
        <div className="min-h-screen bg-[#f8f5f1] py-12 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
                {/* כותרת */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl text-center text-[#937c5d] mb-12 relative font-heebo font-bold
                    after:content-[''] after:absolute after:-bottom-2 after:left-1/2 after:-translate-x-1/2
                    after:w-24 after:h-1 after:bg-[#c9a66c] after:rounded-full">
                        {t('familyForum.title')}
                    </h2>
                    <p className="text-[#4a3f30]/70 text-lg font-heebo">
                        {t('familyForum.subtitle')}
                    </p>
                </div>

                {/* טופס תגובה חדשה - אקורדיון */}
                <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(201,166,108,0.08)] mb-8 overflow-hidden">
                    <button
                        onClick={() => setIsFormOpen(!isFormOpen)}
                        className="w-full p-6 md:p-8 flex items-center justify-between text-xl font-heebo font-bold text-[#937c5d] hover:bg-[#f8f5f1] transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <MessageSquare size={24}/>
                            {t('familyForum.newTopic')}
                        </div>
                        <ChevronDown
                            size={24}
                            className={`transform transition-transform duration-300 ${isFormOpen ? 'rotate-180' : ''}`}
                        />
                    </button>

                    <div className={`transition-all duration-300 ease-in-out ${isFormOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
                        <div className="p-6 md:p-8 pt-0">
                            <form onSubmit={handleCommentSubmit} className="space-y-4" >
                                <div className="flex flex-col md:flex-row gap-4">
                                    <div className="flex-1">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder={t('familyForum.namePlaceholder')}
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                required
                                                className="w-full p-4 pl-12 rounded-xl border-2 border-[#e1ddd7] bg-white/90
                                                    text-[#4a3f30] placeholder-[#937c5d]/60 text-lg font-heebo
                                                    focus:outline-none focus:border-[#c9a66c] focus:ring-2 focus:ring-[#c9a66c]/20
                                                    transition-all duration-300"
                                            />
                                            <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#937c5d]/60" size={20} />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <div className="relative">
                                            <input
                                                type="email"
                                                placeholder={t('familyForum.emailPlaceholder')}
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="w-full p-4 pl-12 rounded-xl border-2 border-[#e1ddd7] bg-white/90
                                                    text-[#4a3f30] placeholder-[#937c5d]/60 text-lg font-heebo
                                                    focus:outline-none focus:border-[#c9a66c] focus:ring-2 focus:ring-[#c9a66c]/20
                                                    transition-all duration-300"
                                            />
                                            <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#937c5d]/60" size={20} />
                                        </div>
                                    </div>
                                </div>

                                <div className="relative">
                                    <textarea
                                        placeholder={t('familyForum.messagePlaceholder')}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        required
                                        rows={4}
                                        className="w-full p-4 rounded-xl border-2 border-[#e1ddd7] bg-white/90
                                            text-[#4a3f30] placeholder-[#937c5d]/60 text-lg font-heebo
                                            focus:outline-none focus:border-[#c9a66c] focus:ring-2 focus:ring-[#c9a66c]/20
                                            transition-all duration-300 resize-none"
                                    />
                                    <MessageSquare className="absolute left-4 top-4 text-[#937c5d]/60" size={20}/>
                                </div>

                                <div className="text-left">
                                    <button
                                        type="submit"
                                        className="px-6 py-3 bg-gradient-to-r from-[#937c5d] to-[#c9a66c]
                                            text-white rounded-xl flex items-center gap-2
                                            hover:from-[#826e52] hover:to-[#b89660] font-heebo
                                            focus:outline-none focus:ring-2 focus:ring-[#c9a66c]/50
                                            transition-all duration-300"
                                    >
                                        <Send size={18} />
                                        {t('familyForum.submitButton')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* רשימת תגובות */}
                <div className="space-y-4">
                    {isLoading ? (
                        <div className="text-center text-[#937c5d]">{t('familyForum.loading')}</div>
                    ) : error ? (
                        <div className="text-center text-red-500">{error}</div>
                    ) : (
                        comments.map((entry) => (
                            <div
                                key={entry._id}
                                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md
                                    transition-all duration-300"
                            >
                                <div className="flex items-start justify-between gap-4 mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#937c5d] to-[#c9a66c]
                                            flex items-center justify-center text-white font-heebo font-bold text-lg">
                                            {entry.name ? entry.name.charAt(0) : '?'}
                                        </div>
                                        <div>
                                            <h4 className="font-heebo text-[#4a3f30]">{entry.name || 'אנונימי'}</h4>
                                            <time className="text-sm font-heebo text-[#937c5d]/70">
                                                {convertToHebrewDate(entry.createdAt)}
                                            </time>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-[#4a3f30] text-lg font-heebo leading-relaxed pr-12">
                                    {entry.content}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default Forum;